import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Mail, MessageCircleMore, Send, ShieldCheck } from 'lucide-react';
import SEO from '../components/SEO';
import { Section, fadeInUp } from '../components/Section';
import { trackLeadEvent } from '../lib/tracking';
import { SOCIAL_URLS } from '../lib/socialLinks';
import { PUBLIC_IMAGES } from '../lib/publicImages';
import { EditorialImageCard } from '../components/EditorialImageCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HELP_ITEMS = [
  '¿Necesitas claridad en tus procesos internos?',
  '¿Quieres mejorar la atención a tus clientes?',
  '¿Buscas una comunicación omnicanal coherente?',
  '¿Quieres que tu empresa respire, recuerde y crezca con confianza?'
];

export default function ContactoPage() {
  const [formData, setFormData] = useState({ nombre: '', email: '', empresa: '', country: '', mensaje: '', source: 'contact_page_form' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        trackLeadEvent({ source: 'contact_page_form' });
        setSubmitted(true);
        setFormData({ nombre: '', email: '', empresa: '', country: '', mensaje: '', source: 'contact_page_form' });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error al enviar el mensaje. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contacto | Neuralia369"
        description="Estamos aquí para escucharte y crecer contigo. Cuéntanos tu reto y te ayudamos a ordenar procesos, atención y comunicación."
        path="/contacto"
      />

      <div className="min-h-screen bg-background pt-24 pb-golden-2xl" data-testid="contacto-page">
        <Section className="pb-golden-xl">
          <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg grid gap-golden-xl lg:grid-cols-[1fr_0.9fr] items-start">
            <motion.div variants={fadeInUp}>
              <p className="editorial-kicker mb-golden-md" data-testid="contacto-kicker">Contacto</p>
              <h1 data-testid="contacto-title">Estamos aquí para <span className="gradient-text">escucharte y crecer contigo</span></h1>
              <p className="mt-golden-md text-lg text-text-body max-w-3xl" data-testid="contacto-intro">
                En Neuralia369 creemos que la comunicación es el inicio de toda transformación. Queremos conocer tus retos,
                tus ideas y tus objetivos para ayudarte a construir un sistema que piense, aprenda, recuerde y te acompañe con claridad en cada paso.
              </p>

              <div className="mt-golden-lg grid gap-3 sm:grid-cols-2" data-testid="contacto-help-grid">
                {HELP_ITEMS.map((item) => (
                  <div key={item} className="rounded-[22px] border border-border-subtle bg-card/35 px-4 py-4 text-sm text-text-body flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-golden-lg grid gap-4 sm:grid-cols-2" data-testid="contacto-canales-grid">
                <a href="mailto:contacto@neuralia369.com" className="glass-card p-5 block hover:border-primary/40 transition-colors" data-testid="contacto-email-card">
                  <Mail className="w-5 h-5 text-primary" />
                  <p className="mt-3 text-text-heading font-heading font-semibold">Email</p>
                  <p className="mt-1 text-sm text-text-body break-all">contacto@neuralia369.com</p>
                </a>
                <a href="https://wa.me/17872981758" target="_blank" rel="noopener noreferrer" className="glass-card p-5 block hover:border-primary/40 transition-colors" data-testid="contacto-whatsapp-card">
                  <MessageCircleMore className="w-5 h-5 text-primary" />
                  <p className="mt-3 text-text-heading font-heading font-semibold">WhatsApp</p>
                  <p className="mt-1 text-sm text-text-body">Canal directo para avanzar más rápido y con menos fricción.</p>
                </a>
              </div>

              <div className="mt-golden-md flex flex-wrap gap-4 text-sm text-text-muted" data-testid="contacto-social-links">
                <a href={SOCIAL_URLS.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Facebook</a>
                <a href={SOCIAL_URLS.youtube} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">YouTube</a>
                <a href={SOCIAL_URLS.tiktok} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">TikTok</a>
              </div>

              <div className="mt-golden-xl">
                <EditorialImageCard
                  image={PUBLIC_IMAGES.contact.hero}
                  alt="Conversación de negocio cercana y profesional"
                  kicker="Cercanía"
                  title="Escuchar primero para proponer mejor"
                  description="La imagen tiene que transmitir que al otro lado hay conversación real, criterio y acompañamiento, no solo automatización." 
                  testId="contacto-editorial-image"
                  minHeight="min-h-[22rem]"
                />
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-card tracing-panel p-golden-xl" data-testid="contacto-form-shell">
              {submitted ? (
                <div className="text-center p-golden-lg" data-testid="contacto-success-state">
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-primary" />
                  </div>
                  <h2 className="mt-golden-md text-2xl text-text-heading font-heading font-semibold">Mensaje recibido</h2>
                  <p className="mt-3 text-text-body">Te responderemos con rapidez, cercanía y una propuesta clara para tu siguiente paso, con foco en tiempo, control y resultados.</p>
                  <Link to="/agenda" className="btn-secondary mt-golden-lg inline-flex" data-testid="contacto-success-agenda-cta">
                    Ver consultoría estratégica
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contacto-form">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Nuestro compromiso</p>
                    <h2 className="mt-2 text-2xl text-text-heading font-heading font-semibold">Responder con claridad y confianza</h2>
                    <p className="mt-2 text-sm text-text-body">Escuchamos antes de proponer, ordenamos antes de automatizar y acompañamos antes de desaparecer.</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <input type="text" className="input-kinesthetic" placeholder="Nombre" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} required data-testid="contacto-nombre" />
                    <input type="email" className="input-kinesthetic" placeholder="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="contacto-email" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <input type="text" className="input-kinesthetic" placeholder="Empresa" value={formData.empresa} onChange={(e) => setFormData({ ...formData, empresa: e.target.value })} data-testid="contacto-empresa" />
                    <input type="text" className="input-kinesthetic" placeholder="País" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} data-testid="contacto-country" />
                  </div>
                  <textarea className="input-kinesthetic min-h-[160px] resize-none" placeholder="Cuéntanos tu reto, objetivo o situación actual" value={formData.mensaje} onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })} required data-testid="contacto-mensaje" />

                  <div className="rounded-[20px] border border-border-subtle bg-card/35 px-4 py-4 flex items-start gap-3 text-sm text-text-body" data-testid="contacto-commitment-card">
                    <ShieldCheck className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span>Responder con rapidez y cercanía. Escuchar antes de proponer soluciones. Acompañarte en cada etapa de tu transformación con criterio y continuidad.</span>
                  </div>

                  <button type="submit" className="btn-primary w-full justify-center" disabled={isSubmitting} data-testid="contacto-submit-button">
                    {isSubmitting ? 'Enviando...' : <><Send className="w-4 h-4" /> Enviar mensaje</>}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </Section>
      </div>
    </>
  );
}